/**
 * Centralized HTTP helpers for installation-order flow (create, edit, list,
 * status transitions, payments, project linking). Import from here instead of
 * inline fetch in installation pages/components.
 */

import {
  fetchNewUniqueId as fetchNewUniqueIdFromSalesOrder,
  resolveSupplierOrganizationId,
  resolveSellerScopeOrganizationId,
  resolveSignageSessionOrganizationId,
  resolveBrokerageOrganizationId,
  resolveOfficeSelectBrokerageOrganizationId,
  persistSupplierOrganizationId,
  appendPermissionsFalse,
  forcePermissionsFalse,
} from "./salesOrderApi";
import { getAuthCookie } from "../utils/authCookies";
import type { License } from "../types";

export const INSTALLATION_API_BASE = process.env.REACT_APP_API_URL || "";

export type InstallationFetchOptions = {
  signal?: AbortSignal;
  /** Return raw Response (caller parses / handles errors). */
  raw?: boolean;
};

function authHeaders(token: string | null | undefined, json = true): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function installationFetch(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: InstallationFetchOptions
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${INSTALLATION_API_BASE}${path}`, {
    ...init,
    headers,
    signal: options?.signal ?? init.signal,
  });
  return res;
}

/** Raw Response helper for callers that parse errors / field-level validation themselves. */
export async function installationRequest(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: InstallationFetchOptions
): Promise<Response> {
  return installationFetch(path, token, init, options);
}

async function installationJson<T = any>(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: InstallationFetchOptions
): Promise<T> {
  const res = await installationFetch(path, token, init, options);
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Request failed (${res.status})`;
    throw new Error(String(message));
  }
  return data as T;
}

// ─── URL builders ───────────────────────────────────────────────────────────

export const installationOrderUrls = {
  orders: () => `/installation_orders`,
  order: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}`,
  update: (orderId: string) => `/installation_orders/update/${encodeURIComponent(orderId)}`,
  /** POST — seller signage organization id (was signage_id / user id). */
  dynamicCost: (organizationId: string) =>
    `/installation_orders/dynamic_cost/${encodeURIComponent(organizationId)}`,
  orgDatatable: (organizationId: string) =>
    `/installation_orders/organizations/${encodeURIComponent(organizationId)}/datatable`,
  orgExportCsv: (organizationId: string) =>
    `/installation_orders/organizations/${encodeURIComponent(organizationId)}/export/csv`,
  orgProductionOverviewDatatable: (organizationId: string) =>
    `/installation_orders/organizations/${encodeURIComponent(organizationId)}/production_overview/datatable`,
  orgOrders: (organizationId: string) =>
    `/installation_orders/organizations/${encodeURIComponent(organizationId)}`,
  datatable: () => `/installation_orders/datatable`,
  /** GET — installer membership id (was user_id). */
  scheduledJobs: (membershipId: string, date: string) =>
    `/installation_orders/${encodeURIComponent(membershipId)}/${encodeURIComponent(date)}/scheduled_jobs`,
  search: (entityId: string, query = "") =>
    `/installation_orders/search/${encodeURIComponent(entityId)}${query ? `?${query}` : ""}`,
  sourceByMembership: (membershipId: string) =>
    `/installation_orders/source/${encodeURIComponent(membershipId)}`,
  finalize: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/finalize`,
  schedule: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/schedule`,
  reschedule: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/reschedule`,
  inProgress: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/in_progress`,
  complete: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/complete`,
  cancel: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/cancel`,
  payment: (orderId: string) => `/installation_orders/${encodeURIComponent(orderId)}/payment`,
  net30Payment: (orderId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/net30_payment`,
  uploadProof: (orderId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/upload-proof`,
  updateProductionStatus: (orderId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/update_production_status`,
  lineItems: (orderId: string, organizationId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/organizations/${encodeURIComponent(organizationId)}/line_items`,
  binAllocationSummary: (orderId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/bin_allocation_summary`,
  purchasedProducts: (orderId: string) =>
    `/installation_orders/${encodeURIComponent(orderId)}/purchased_products`,
  publicOrder: (orderId: string, query = "") =>
    `/public/installation_orders/${encodeURIComponent(orderId)}${query ? `?${query}` : ""}`,
  publicPayment: (orderId: string, query = "") =>
    `/public/installation_orders/${encodeURIComponent(orderId)}/payment${query ? `?${query}` : ""}`,
  /** GET available qty — inv_source is "agent" | "office"; entity_id is membership id. */
  inventoryEntityProduct: (invSource: string, entityId: string, productId: string) =>
    `/inventory/${encodeURIComponent(invSource)}/entities/${encodeURIComponent(entityId)}/product/${encodeURIComponent(productId)}`,
  /** @deprecated Use inventoryEntityProduct — legacy user id route removed from backend. */
  inventoryProduct: (userType: string, userId: string, productId: string) =>
    `/inventory/${encodeURIComponent(userType)}/user/${encodeURIComponent(userId)}/product/${encodeURIComponent(productId)}`,
  inventoryByType: (inventoryType: string, userId: string, productId: string) =>
    `/${encodeURIComponent(inventoryType)}/${encodeURIComponent(userId)}/inventory/${encodeURIComponent(productId)}`,
  agentInventory: (agentId: string, productId: string) =>
    `/agent/${encodeURIComponent(agentId)}/inventory/${encodeURIComponent(productId)}`,
  officeInventory: (officeId: string, productId: string) =>
    `/office/${encodeURIComponent(officeId)}/inventory/${encodeURIComponent(productId)}`,
  signageInventory: (userType: string, userId: string, signageId: string) =>
    `/inventory/${encodeURIComponent(userType)}/${encodeURIComponent(userId)}/signage/${encodeURIComponent(signageId)}`,
  rotationalProducts: (organizationId: string) =>
    `/signage/${encodeURIComponent(organizationId)}/rotational-products`,
  rotationalSerializedUnits: (
    organizationId: string,
    rotationalId: string,
    warehouseId: string
  ) =>
    `/signage/${encodeURIComponent(organizationId)}/warehouses/rotational-pool/${encodeURIComponent(rotationalId)}/warehouse/${encodeURIComponent(warehouseId)}/available-serialized-units`,
  installationLocations: () => `/installation_locations`,
  installationLocation: (locationId: string) =>
    `/installation_locations/${encodeURIComponent(locationId)}`,
  specialFeesByOrganizationMembership: (organizationId: string, membershipId: string) =>
    `/special-fees/organizations/${encodeURIComponent(organizationId)}/by-membership/${encodeURIComponent(membershipId)}`,
  specialFeesEligibleForInstallationOrder: (orderId: string) =>
    `/special-fees/installation-orders/${encodeURIComponent(orderId)}/eligible`,
  specialFeesEligibleForRemovalOrder: (orderId: string) =>
    `/special-fees/removal-orders/${encodeURIComponent(orderId)}/eligible`,
  imagesUpload: () => `/images/upload`,
  category: (categoryId: string) => `/categories/${encodeURIComponent(categoryId)}`,
  organizationProfilesSearch: (query: string) =>
    `/organization-profiles/search${query.startsWith("?") ? query : query ? `?${query}` : ""}`,
  paymentGateway: (organizationId: string) =>
    `/payments/gateway/organizations/${encodeURIComponent(organizationId)}`,
  stripePaymentIntent: (organizationId: string) =>
    `/payments/stripe/organizations/${encodeURIComponent(organizationId)}/payment-intent`,
  paymentMethods: (organizationId: string, membershipId: string) =>
    `/payment-methods/organizations/${encodeURIComponent(organizationId)}/memberships/${encodeURIComponent(membershipId)}`,
  paymentMethodsCount: (organizationId: string, membershipId: string) =>
    `/payment-methods/count/organizations/${encodeURIComponent(organizationId)}/memberships/${encodeURIComponent(membershipId)}`,
};

/** Resolve seller signage organization id for org-scoped installation routes. */
export function resolveInstallationSellerOrganizationId(
  fallbackSignageOrOrgId?: string | null,
  order?: { seller_org_id?: string; signage_id?: string } | null
): string {
  return resolveSellerScopeOrganizationId(fallbackSignageOrOrgId, order);
}

export type InstallationOrderIdentity = {
  inventory_source?: string;
  user_type?: string;
  signage_id?: string;
  seller_org_id?: string;
  seller_membership_id?: string;
  signage_company_name?: string;
  agent_id?: string;
  agent_org_id?: string;
  agent_membership_id?: string;
  agent_name?: string;
  team_member_id?: string;
  team_member_org_id?: string;
  team_member_membership_id?: string;
  team_member_name?: string;
  buyer_membership_id?: string;
  source_membership_id?: string;
  installation_details?: {
    /** @deprecated Removed from backend InstallationDetails — use installer_membership_id */
    installer_id?: string;
    installer_name?: string;
    installer_org_id?: string;
    installer_membership_id?: string;
    installer_global_user_id?: string;
    assignee_org_id?: string;
    assignee_membership_id?: string;
    assignee_global_user_id?: string;
    contact_number?: string;
  };
  billing_address?: { _id?: string; address_details?: { _id?: string } };
};

/** Foundation or legacy agent membership id for address/supplier routes. */
export function resolveInstallationAgentMembershipId(
  order?: InstallationOrderIdentity | null
): string {
  if (!order) return "";
  return String(
    order.agent_membership_id ?? order.agent_id ?? order.buyer_membership_id ?? ""
  ).trim();
}

/** Foundation or legacy team member membership id. */
export function resolveInstallationTeamMemberMembershipId(
  order?: InstallationOrderIdentity | null
): string {
  if (!order) return "";
  return String(
    order.team_member_membership_id ?? order.team_member_id ?? ""
  ).trim();
}

/** membership_id for GET /organization-memberships/{membership_id}/addresses on view/edit. */
export function resolveInstallationAddressMembershipId(
  order?: InstallationOrderIdentity | null
): string {
  if (!order) return "";
  const isOffice =
    String(order.inventory_source || "").trim().toLowerCase() === "office";
  if (isOffice) {
    const teamMember = resolveInstallationTeamMemberMembershipId(order);
    if (teamMember) return teamMember;
  }
  const agent = resolveInstallationAgentMembershipId(order);
  if (agent) return agent;
  return String(order.buyer_membership_id ?? order.source_membership_id ?? "").trim();
}

/** Vendor option for react-select from saved installation order. */
export function resolveInstallationVendorSelectOption(
  order?: InstallationOrderIdentity | null
): {
  value: string;
  label: string;
  membershipId?: string;
  globalUserId?: string;
  organizationId?: string;
} | null {
  if (!order) return null;
  const sellerOrgId = resolveInstallationSellerOrganizationId(
    order.signage_id,
    order
  );
  const details = order.installation_details;
  const membershipId = String(
    details?.installer_membership_id ?? details?.assignee_membership_id ?? ""
  ).trim();
  const globalUserId = String(
    details?.installer_global_user_id ?? details?.assignee_global_user_id ?? ""
  ).trim();
  const organizationId = String(
    details?.installer_org_id ?? details?.assignee_org_id ?? sellerOrgId ?? ""
  ).trim();
  const label = String(
    order.signage_company_name ?? details?.installer_name ?? ""
  ).trim();
  if (organizationId || sellerOrgId) {
    const value = organizationId || sellerOrgId;
    return {
      value,
      label: label || value,
      organizationId: value,
      ...(membershipId ? { membershipId } : {}),
      ...(globalUserId ? { globalUserId } : {}),
    };
  }
  const legacyId = String(details?.installer_id ?? "").trim();
  const legacyLabel = String(details?.installer_name ?? "").trim();
  if (legacyId && legacyLabel) {
    return { value: legacyId, label: legacyLabel, organizationId: legacyId };
  }
  return null;
}

/** Vendor / installer membership id for GET /organization-memberships/{id}. */
export function resolveInstallationSellerMembershipId(
  order?: InstallationOrderIdentity | null,
  selectedInstaller?: { membershipId?: string } | null
): string {
  return String(
    selectedInstaller?.membershipId ||
      order?.seller_membership_id ||
      order?.installation_details?.installer_membership_id ||
      order?.installation_details?.assignee_membership_id ||
      ""
  ).trim();
}

/** Preferred scheduler id from installation_details (membership, not legacy installer_id). */
export function resolveInstallationSchedulerMembershipId(
  details?: InstallationOrderIdentity["installation_details"] | null
): string {
  if (!details) return "";
  return String(
    details.installer_membership_id ??
      details.assignee_membership_id ??
      details.installer_id ??
      ""
  ).trim();
}

export function resolveInstallationInstallerSelectOption(
  order?: InstallationOrderIdentity | null,
  suppliers?: Array<{ supplier_id?: string; name?: string }> | null
): {
  value: string;
  label: string;
  membershipId?: string;
  globalUserId?: string;
  organizationId?: string;
} | null {
  const sellerOrgId = resolveInstallationSellerOrganizationId(
    order?.signage_id,
    order
  );
  const vendor = resolveInstallationVendorSelectOption(order);
  if (sellerOrgId && suppliers?.length) {
    const match = suppliers.find((sup) => sup.supplier_id === sellerOrgId);
    if (match?.supplier_id) {
      return {
        value: match.supplier_id,
        label: match.name || sellerOrgId,
        organizationId: match.supplier_id,
        ...(vendor?.membershipId ? { membershipId: vendor.membershipId } : {}),
        ...(vendor?.globalUserId ? { globalUserId: vendor.globalUserId } : {}),
      };
    }
  }
  return vendor;
}

/** Ensure saved billing address appears in address dropdown options. */
export function mergeInstallationOrderBillingAddress<
  T extends { _id?: string; address_details?: { _id?: string } },
>(addressObjects: T[], billingAddress?: T | null): T[] {
  if (!billingAddress?._id) return addressObjects;
  const billingId = String(billingAddress._id);
  const exists = addressObjects.some(
    (addr) =>
      String(addr._id ?? "") === billingId ||
      String(addr.address_details?._id ?? "") === billingId
  );
  if (exists) return addressObjects;
  return [billingAddress, ...addressObjects];
}

/**
 * Buyer membership id for GET /payment-methods/organizations/{org}/memberships/{membership_id}
 * and saved-card membership scoping.
 * - office inventory → team_member_membership_id
 * - otherwise → agent_membership_id
 * Does not use legacy agent_id / team_member_id.
 */
export function resolveInstallationPaymentMethodsMembershipId(
  order?: {
    inventory_source?: string;
    buyer_membership_id?: string;
    customer_membership_id?: string;
    agent_membership_id?: string;
    team_member_membership_id?: string;
    customer_id?: string;
    source_membership_id?: string;
  } | null,
  sessionMembershipIdFallback?: string | null
): string {
  const sessionMembershipId = String(
    sessionMembershipIdFallback ?? getAuthCookie("membershipId") ?? ""
  ).trim();

  if (!order) return sessionMembershipId;

  const isOffice = String(order.inventory_source || "").trim().toLowerCase() === "office";
  const byInventorySource = isOffice
    ? String(order.team_member_membership_id ?? "").trim()
    : String(order.agent_membership_id ?? "").trim();
  if (byInventorySource) return byInventorySource;

  const preferred = String(
    order.buyer_membership_id ??
      order.customer_membership_id ??
      order.source_membership_id ??
      order.customer_id ??
      ""
  ).trim();
  if (preferred) return preferred;

  return sessionMembershipId;
}

type InstallationInventoryMembershipOption = {
  membershipId?: string;
  membership_id?: string;
  value?: string;
} | null;

/**
 * Membership id for GET /inventory/{inv_source}/entities/{entity_id}/product/{product_id}.
 * - office → team_member_membership_id
 * - agent → agent_membership_id
 * When picker context is active, resolves from selected agent/team-member options instead of order ids.
 */
export function resolveInstallationInventoryEntityMembershipId(options: {
  order?: Parameters<typeof resolveInstallationPaymentMethodsMembershipId>[0] | null;
  invSource?: "agent" | "office" | null;
  agentOption?: InstallationInventoryMembershipOption;
  teamMemberOption?: InstallationInventoryMembershipOption;
  sessionMembershipIdFallback?: string | null;
}): string {
  const {
    order,
    invSource,
    agentOption,
    teamMemberOption,
    sessionMembershipIdFallback,
  } = options;

  if (order) {
    return resolveInstallationPaymentMethodsMembershipId(
      order,
      sessionMembershipIdFallback
    );
  }

  if (invSource === "office") {
    const tm = teamMemberOption;
    return String(tm?.membershipId ?? tm?.membership_id ?? tm?.value ?? "").trim();
  }

  if (invSource === "agent") {
    const agent = agentOption;
    return String(
      agent?.membershipId ?? agent?.membership_id ?? agent?.value ?? ""
    ).trim();
  }

  return String(
    sessionMembershipIdFallback ?? getAuthCookie("membershipId") ?? ""
  ).trim();
}

export {
  resolveSupplierOrganizationId,
  resolveSellerScopeOrganizationId,
  resolveSignageSessionOrganizationId,
  resolveBrokerageOrganizationId,
  resolveOfficeSelectBrokerageOrganizationId,
  persistSupplierOrganizationId,
};

/** Unwrap `{ data: order }` envelopes returned by some installation GET routes. */
export function unwrapInstallationOrderPayload(raw: unknown): any {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const data = r.data;
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    ((data as Record<string, unknown>)._id ||
      (data as Record<string, unknown>).id ||
      (data as Record<string, unknown>).order_number)
  ) {
    return data as Record<string, unknown>;
  }
  return r;
}

/**
 * Public GET order may return `{ order: {...}, project_settings?: {...} }`.
 * UI expects one object with order fields + optional `project_settings`.
 */
export function normalizePublicOrderOrInstallationPayload(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const r = raw as { order?: Record<string, unknown>; project_settings?: Record<string, unknown> };
  const inner = r.order;
  if (inner && typeof inner === "object") {
    const ps = r.project_settings;
    return {
      ...inner,
      project_settings:
        ps && typeof ps === "object"
          ? ps
          : (inner as { project_settings?: Record<string, unknown> }).project_settings,
    };
  }
  return raw;
}

// ─── Installation orders ────────────────────────────────────────────────────

export async function createInstallationOrder(
  token: string | null | undefined,
  body: unknown,
  options?: InstallationFetchOptions
): Promise<Response> {
  return installationRequest(
    installationOrderUrls.orders(),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchInstallationOrderById(
  token: string | null | undefined,
  orderId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.order(orderId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationOrderByIdUnwrapped(
  token: string | null | undefined,
  orderId: string,
  options?: InstallationFetchOptions
) {
  const raw = await fetchInstallationOrderById(token, orderId, options);
  return unwrapInstallationOrderPayload(raw);
}

export async function patchInstallationOrder(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  options?: InstallationFetchOptions
): Promise<Response> {
  return installationRequest(
    installationOrderUrls.update(orderId),
    token,
    { method: "PATCH", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function postInstallationDynamicCost(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.dynamicCost(organizationId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchInstallationOrdersOrgDatatable(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.orgDatatable(organizationId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function exportInstallationOrdersOrgCsv(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: InstallationFetchOptions
): Promise<Response> {
  return installationRequest(
    installationOrderUrls.orgExportCsv(organizationId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchInstallationOrdersDatatable(
  token: string | null | undefined,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.datatable(),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchInstallationScheduledJobs(
  token: string | null | undefined,
  membershipId: string,
  date: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.scheduledJobs(membershipId, date),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function postInstallationOrderAction(
  token: string | null | undefined,
  orderId: string,
  action: "finalize" | "schedule" | "reschedule" | "in_progress" | "complete" | "cancel",
  body?: unknown,
  options?: InstallationFetchOptions
): Promise<Response> {
  const pathByAction: Record<typeof action, string> = {
    finalize: installationOrderUrls.finalize(orderId),
    schedule: installationOrderUrls.schedule(orderId),
    reschedule: installationOrderUrls.reschedule(orderId),
    in_progress: installationOrderUrls.inProgress(orderId),
    complete: installationOrderUrls.complete(orderId),
    cancel: installationOrderUrls.cancel(orderId),
  };
  return installationRequest(
    pathByAction[action],
    token,
    {
      method: "POST",
      headers: authHeaders(token),
      ...(body != null ? { body: JSON.stringify(body) } : {}),
    },
    options
  );
}

export async function postInstallationOrderPayment(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  method: "POST" | "PUT" = "POST",
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.payment(orderId),
    token,
    { method, headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function postInstallationOrderNet30Payment(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.net30Payment(orderId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function putInstallationOrderUploadProof(
  token: string | null | undefined,
  orderId: string,
  body: FormData,
  options?: InstallationFetchOptions
): Promise<Response> {
  return installationRequest(
    installationOrderUrls.uploadProof(orderId),
    token,
    { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body },
    options
  );
}

export async function patchInstallationOrderLineItems(
  token: string | null | undefined,
  orderId: string,
  organizationId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.lineItems(orderId, organizationId),
    token,
    { method: "PATCH", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchInstallationPaymentMethods(
  token: string | null | undefined,
  organizationId: string,
  membershipId: string,
  options?: InstallationFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  const memId = String(membershipId || "").trim();
  if (!orgId || !memId) {
    return [];
  }
  return installationJson(
    installationOrderUrls.paymentMethods(organizationId, membershipId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationPaymentGateway(
  token: string | null | undefined,
  organizationId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    appendPermissionsFalse(installationOrderUrls.paymentGateway(organizationId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function createInstallationStripePaymentIntent(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  const amountCents = Number((body as { amount?: unknown } | null)?.amount ?? 0);
  if (!(amountCents > 0)) {
    // Stripe rejects amount 0 — skip the network call for fully paid / $0 balances.
    return { skipped: true, clientSecret: null, client_secret: null };
  }
  return installationJson(
    installationOrderUrls.stripePaymentIntent(organizationId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function updateInstallationOrderProductionStatus(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.updateProductionStatus(orderId),
    token,
    { method: "PUT", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function searchCompletedInstallationOrders(
  token: string | null | undefined,
  entityId: string,
  queryString = "",
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.search(entityId, queryString),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

// ─── Public installation orders ─────────────────────────────────────────────

/** GET /public/installation_orders/:installationOrderId?token= */
export async function fetchPublicInstallationOrderById(
  token: string | null | undefined,
  _projectId: string,
  installationOrderId: string,
  publicToken: string
): Promise<any> {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await installationRequest(
    installationOrderUrls.publicOrder(installationOrderId, qs.toString()),
    token,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to load public installation order";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public installation order");
  }
  return normalizePublicOrderOrInstallationPayload(raw);
}

/** POST /public/installation_orders/:installationOrderId/payment?token= */
export async function postPublicInstallationOrderPayment(
  _projectId: string,
  installationOrderId: string,
  publicToken: string,
  body: Record<string, unknown>
): Promise<Response> {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const q = qs.toString();
  return fetch(
    `${INSTALLATION_API_BASE}${installationOrderUrls.publicPayment(installationOrderId, q)}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
}

// ─── Inventory / products (installation picker) ─────────────────────────────

export async function fetchInstallationInventoryProduct(
  token: string | null | undefined,
  invSource: "agent" | "office",
  entityId: string,
  productId: string,
  options?: InstallationFetchOptions
) {
  return installationJson<{ available?: number }>(
    installationOrderUrls.inventoryEntityProduct(invSource, entityId, productId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationInventoryByType(
  token: string | null | undefined,
  inventoryType: string,
  userId: string,
  productId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.inventoryByType(inventoryType, userId, productId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationAgentInventory(
  token: string | null | undefined,
  agentId: string,
  productId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.agentInventory(agentId, productId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationOfficeInventory(
  token: string | null | undefined,
  officeId: string,
  productId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.officeInventory(officeId, productId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationSignageInventory(
  token: string | null | undefined,
  userType: string,
  userId: string,
  signageId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.signageInventory(userType, userId, signageId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchSignageRotationalProducts(
  token: string | null | undefined,
  organizationId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.rotationalProducts(organizationId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchRotationalSerializedUnits(
  token: string | null | undefined,
  organizationId: string,
  rotationalId: string,
  warehouseId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.rotationalSerializedUnits(organizationId, rotationalId, warehouseId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

// ─── Locations / fees / misc ────────────────────────────────────────────────

export async function fetchInstallationLocations(
  token: string | null | undefined,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.installationLocations(),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationLocationById(
  token: string | null | undefined,
  locationId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.installationLocation(locationId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationSpecialFeesByMembership(
  token: string | null | undefined,
  organizationId: string,
  membershipId: string,
  options?: InstallationFetchOptions
): Promise<Response> {
  const orgId = String(organizationId || "").trim();
  const memberId = String(membershipId || "").trim();
  return installationRequest(
    forcePermissionsFalse(installationOrderUrls.specialFeesByOrganizationMembership(orgId, memberId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationSpecialFeesEligibleForOrder(
  token: string | null | undefined,
  orderId: string,
  orderType: "installation" | "removal",
  options?: InstallationFetchOptions
): Promise<Response> {
  const path =
    orderType === "removal"
      ? installationOrderUrls.specialFeesEligibleForRemovalOrder(orderId)
      : installationOrderUrls.specialFeesEligibleForInstallationOrder(orderId);
  return installationRequest(
    forcePermissionsFalse(path),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export function getInstallationImageUploadUrl(): string {
  return `${INSTALLATION_API_BASE}${installationOrderUrls.imagesUpload()}`;
}

export async function deleteCategoryById(
  token: string | null | undefined,
  categoryId: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.category(categoryId),
    token,
    { method: "DELETE", headers: authHeaders(token) },
    options
  );
}

export async function searchOrganizationProfiles(
  token: string | null | undefined,
  queryString: string,
  options?: InstallationFetchOptions
) {
  return installationJson(
    installationOrderUrls.organizationProfilesSearch(queryString),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchInstallationDraftUniqueId(token: string): Promise<string> {
  const data = await fetchNewUniqueIdFromSalesOrder(token);
  const id =
    (data as any)?.id ||
    (data as any)?.data?.id ||
    (data as any)?.data?.unique_id ||
    (data as any)?.unique_id ||
    null;
  if (!id) throw new Error("Unique ID not found in API response");
  return String(id);
}

// ─── Shared checkout helpers (re-export from sales order API) ───────────────

export {
  SALES_ORDER_API_BASE,
  salesOrderUrls,
  fetchUserAddresses,
  resolveMembershipAddressesRouteId,
  fetchUserById,
  searchUsers,
  fetchSignageAgentsSelect2,
  fetchOfficeSelect,
  fetchOfficeTeamMembersSelect,
  fetchOfficeTeamMembersSelect2,
  fetchBrokerageAgentsByOfficeIdsSelect2,
  fetchBrokerageTeamMemberOfficesSelect2,
  fetchAgentSelect,
  fetchOfficeById,
  fetchBrokerageFirms,
  fetchAgentsByOfficeId,
  fetchAgentSuppliers,
  fetchNewUniqueId,
  uploadDocumentsToOrder,
  syncUserEmailToDocument,
  fetchPaymentMethodsBySupplierAndUser,
  createStripePaymentIntent,
  fetchPaymentGatewayBySupplier,
  fetchGeneralSettings,
} from "./salesOrderApi";

// ─── Project / quote helpers used by installation flow ──────────────────────

/**
 * Minimal logged-in user profile from auth cookies.
 * Replaces GET /users/{id} on installation pages.
 */
export function sessionLicenseFromAuthCookies(): License {
  const userId = String(getAuthCookie("userId") || "").trim();
  const userName = String(getAuthCookie("userName") || "").trim();
  const email = String(getAuthCookie("email") || "").trim();
  const licenseType = String(getAuthCookie("licenseType") || "").trim();
  const userType = String(getAuthCookie("userType") || "").trim();
  const organizationId = String(getAuthCookie("organizationId") || "").trim();
  const membershipId = String(getAuthCookie("membershipId") || "").trim();
  const globalUserId = String(getAuthCookie("globalUserId") || "").trim();
  const supplierId =
    typeof localStorage !== "undefined"
      ? String(getAuthCookie("supplierId") || localStorage.getItem("supplierId") || "").trim()
      : String(getAuthCookie("supplierId") || "").trim();
  const signageOrgId = resolveSignageSessionOrganizationId();
  const parentSignageId = signageOrgId || supplierId || organizationId || userId;

  const nameParts = userName.split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    id: userId,
    vid: 0,
    email,
    userType,
    isProfileUpdated: true,
    firstName,
    lastName,
    phone1: "",
    phone2: "",
    jobTitle: "",
    photo: "",
    firmDetails: { companyName: userName } as License["firmDetails"],
    statuss: "",
    licenseValidity: null,
    billingCycle: null,
    billingCyclePrice: null,
    paymentMethod: "",
    licenseType,
    modules: [],
    profilePic: "",
    offices: [],
    created_by: {
      id: parentSignageId,
      name: userName,
    },
    // foundation ids for downstream payloads when needed
    ...(membershipId ? { membership_id: membershipId } : {}),
    ...(globalUserId ? { global_user_id: globalUserId } : {}),
    ...(organizationId ? { organization_id: organizationId } : {}),
  } as License;
}

export {
  ORDER_QUOTE_MODE_SETTING_KEY,
  INSTALLATION_ORDER_NOTIFICATION_EMAILS_KEY,
  fetchOrderQuoteModeSetting,
  fetchPublicOrderQuoteModeSetting,
  fetchProjectById,
  fetchProjectsSelect2,
  fetchProjectApprovedMessage,
  fetchPublicProjectMessagesByProjectId,
  linkProjectQuote,
  unlinkProjectQuote,
  fetchProjectPublicAccess,
  resolveApprovedProofVersionSummary,
  fetchPaymentGatewaysCreatedByUserId,
} from "components/proejctsandquoteapicalls";

export type {
  SignageProjectDetail,
  ProjectApprovedMessageResponse,
  ProjectPublicAccessResponse,
  GeneralSettingByKeyRow,
  GeneralSettingByKeyResponse,
  ProjectMessageRow,
} from "components/proejctsandquoteapicalls";
